import { Layout } from "@/components/layout/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TESTIDS } from "@/constants/testIds";
import { Users, Cpu, Wrench, ChevronRight } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { RULESETS, STATUS } from "@/game/rulesets";

const DIFFS = [
  { id: "beginner", title: "Easy", body: "Quick, mostly legal moves with a soft preference for immediate captures. Great for learning the flow.", testId: TESTIDS.difficultyBeginner },
  { id: "intermediate", title: "Medium", body: "Looks two moves ahead, considers exposed columns and singletons, punishes obvious mistakes.", testId: TESTIDS.difficultyIntermediate },
  { id: "advanced", title: "Hard", body: "Iterative-deepening search up to ~6 plies with alpha-beta and capture ordering. Strong, repeatable play.", testId: TESTIDS.difficultyAdvanced },
];

const PLAYABLE_VARIANTS = RULESETS.filter((r) => r.status === STATUS.PLAYABLE);

export function PlayMode() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("single");
  const [difficulty, setDifficulty] = useState("intermediate");
  const [humanSide, setHumanSide] = useState("A");
  const [direction, setDirection] = useState("CCW");
  const [variantId, setVariantId] = useState("zimbabwe-standard");

  const start = () => {
    const params = new URLSearchParams({ mode, difficulty, side: humanSide, dir: direction });
    const path = variantId === "zimbabwe-standard" ? "/play/standard" : `/play/${variantId}`;
    navigate(`${path}?${params.toString()}`);
  };

  return (
    <Layout>
      <section
        data-testid={TESTIDS.playModePage}
        className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
      >
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Choose how to play</p>
        <h1 className="font-display text-4xl sm:text-5xl mt-1">Game modes</h1>
        <p className="mt-3 text-foreground/80 max-w-xl">
          Pick a country variant, then a mode and difficulty. Each variant
          uses its own independent rules engine.
        </p>

        <div className="mt-8">
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Country variant</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {PLAYABLE_VARIANTS.map((v) => (
              <button
                key={v.id}
                data-testid={`variant-${v.id}`}
                onClick={() => setVariantId(v.id)}
                className={cn(
                  "text-left p-4 rounded-2xl border transition-colors",
                  variantId === v.id ? "border-primary bg-primary/5 ring-2 ring-primary/30" : "border-border hover:border-primary/50"
                )}
              >
                <div className="flex items-center justify-between">
                  <p className="font-display text-lg">{v.name}</p>
                  <Badge variant="outline" className="rounded-full text-[10px]">{v.country}</Badge>
                </div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                  {v.rows}×{v.pitsPerRow} · {v.counters} counters
                </p>
                <p className="text-xs text-muted-foreground mt-2">{v.summary}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          <ModeCard
            testId={TESTIDS.modeSingleBtn}
            active={mode === "single"}
            onClick={() => setMode("single")}
            icon={<Cpu className="h-5 w-5" />}
            title="Single player"
            body="Play against the computer with three difficulties."
          />
          <ModeCard
            testId={TESTIDS.modeLocalBtn}
            active={mode === "local"}
            onClick={() => setMode("local")}
            icon={<Users className="h-5 w-5" />}
            title="Local 2-player"
            body="Two humans take turns on the same device."
          />
          <ModeCard
            testId={TESTIDS.modePracticeBtn}
            active={mode === "practice"}
            onClick={() => navigate("/practice")}
            icon={<Wrench className="h-5 w-5" />}
            title="Practice mode"
            body="Build a position, try moves, inspect the engine."
          />
        </div>

        {mode === "single" && (
          <div className="mt-10">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Difficulty</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {DIFFS.map((d) => (
                <button
                  key={d.id}
                  data-testid={d.testId}
                  onClick={() => setDifficulty(d.id)}
                  className={cn(
                    "text-left p-4 rounded-2xl border transition-colors",
                    difficulty === d.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                  )}
                >
                  <div className="flex items-center justify-between">
                    <p className="font-display text-lg">{d.title}</p>
                    {difficulty === d.id && (
                      <Badge className="rounded-full">Selected</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{d.body}</p>
                </button>
              ))}
            </div>

            <p className="text-xs uppercase tracking-widest text-muted-foreground mt-8 mb-3">Your side</p>
            <div className="flex gap-2">
              <Button
                variant={humanSide === "A" ? "default" : "outline"}
                className="rounded-full"
                onClick={() => setHumanSide("A")}
              >
                Play as Player 1 (bottom)
              </Button>
              <Button
                variant={humanSide === "B" ? "default" : "outline"}
                className="rounded-full"
                onClick={() => setHumanSide("B")}
              >
                Play as Player 2 (top)
              </Button>
            </div>
          </div>
        )}

        <div className="mt-10">
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
            Sowing direction
            <span className="ml-2 normal-case tracking-normal text-muted-foreground/80">
              · the starting player chooses; this is maintained throughout the game
            </span>
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              data-testid="direction-ccw"
              onClick={() => setDirection("CCW")}
              className={cn(
                "text-left p-4 rounded-2xl border transition-colors flex items-start gap-3",
                direction === "CCW" ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
              )}
            >
              <span className="text-2xl mt-0.5">↺</span>
              <div>
                <p className="font-display text-lg">Anti-clockwise</p>
                <p className="text-xs text-muted-foreground mt-1">
                  The traditional Zimbabwean direction. Outer row plays left → right.
                </p>
              </div>
            </button>
            <button
              data-testid="direction-cw"
              onClick={() => setDirection("CW")}
              className={cn(
                "text-left p-4 rounded-2xl border transition-colors flex items-start gap-3",
                direction === "CW" ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
              )}
            >
              <span className="text-2xl mt-0.5">↻</span>
              <div>
                <p className="font-display text-lg">Clockwise</p>
                <p className="text-xs text-muted-foreground mt-1">
                  The mirror-image game. Outer row plays right → left.
                </p>
              </div>
            </button>
          </div>
        </div>

        <div className="mt-10 flex flex-wrap gap-3">
          <Button
            data-testid={TESTIDS.startGameBtn}
            size="lg"
            onClick={start}
            className="rounded-full px-7"
          >
            Start game <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
          <Button variant="ghost" onClick={() => navigate("/")} className="rounded-full">
            Cancel
          </Button>
        </div>
      </section>
    </Layout>
  );
}

function ModeCard({ testId, active, onClick, icon, title, body }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={cn(
        "text-left p-5 rounded-2xl border transition-all",
        active ? "border-primary ring-2 ring-primary/30 bg-primary/5" : "border-border hover:border-primary/50"
      )}
    >
      <div className="flex items-center gap-2">
        <span className="p-1.5 rounded-lg bg-primary/10 text-primary">{icon}</span>
        <p className="font-display text-xl">{title}</p>
      </div>
      <p className="text-sm text-muted-foreground mt-2">{body}</p>
    </button>
  );
}
